import { useState, useEffect } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

type MapPickerProps = {
  onChange: (location: { lat: number; lng: number }) => void;
  defaultLocation?: { lat: number; lng: number };
  height?: string;
  disableDefaultUI?: boolean;
  disableZoom?: boolean;
  disableDragging?: boolean;
};

const MapPicker: React.FC<MapPickerProps> = ({
  onChange,
  defaultLocation,
  height,
  disableDefaultUI = false,
  disableZoom = false,
  disableDragging = false,
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_URL,
    libraries: ["places"],
  });

  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [markerPosition, setMarkerPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  useEffect(() => {
    if (!defaultLocation?.lat || !defaultLocation?.lng) return;

    setCenter(defaultLocation);
    setMarkerPosition(defaultLocation);
  }, [defaultLocation]);

  // Fetch the user's current location
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCenter(userLocation);
        setMarkerPosition(userLocation);
        onChange(userLocation);
      },
      () => {
        console.error("Unable to retrieve location. Using default location.");
        const fallbackLocation = { lat: 37.7749, lng: -122.4194 };
        setCenter(fallbackLocation);
        setMarkerPosition(fallbackLocation);
        onChange(fallbackLocation);
      }
    );
  }, []);

  const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setMarkerPosition(newPosition);
      onChange(newPosition);
    }
  };

  if (!isLoaded || !center) return <div>Loading...</div>;

  return (
    <GoogleMap
      center={center}
      zoom={15}
      mapContainerStyle={{
        width: "100%",
        height: height || "240px",
        borderRadius: "10px",
      }}
      options={{
        disableDefaultUI,
        gestureHandling: disableDragging ? "none" : "auto",
        scrollwheel: !disableZoom,
      }}
    >
      {markerPosition && (
        <Marker
          position={markerPosition}
          draggable={!disableDragging}
          onDragEnd={handleMarkerDragEnd}
          opacity={disableDragging ? 0 : 1}
        />
      )}
    </GoogleMap>
  );
};

export default MapPicker;
